import { repairImageKitUrl } from '@dupe/common/constants'
import { useState } from 'react'
import { ImageOff } from 'lucide-react'
import Image from 'next/image'

interface SeoReportHeroImageProps {
  url?: string | null
  alt: string
  isGenerating?: boolean
}

function canUseNextImage(url: string): boolean {
  if (url.startsWith('/')) return true

  try {
    const hostname = new URL(url).hostname
    return hostname === 'ik.imagekit.io' || hostname === 'assets.dupe.com'
  } catch {
    return false
  }
}

export function SeoReportHeroImage({
  url,
  alt,
  isGenerating = false,
}: SeoReportHeroImageProps) {
  const [hasError, setHasError] = useState(false)
  const imgUrl = url != null ? repairImageKitUrl(url) : null
  const useNextImage = imgUrl != null ? canUseNextImage(imgUrl) : false

  return (
    <section className='-mx-4 overflow-hidden rounded-none md:-mx-6 md:rounded-3xl'>
      <div className='relative aspect-[16/9] w-full'>
        {imgUrl == null || hasError ? (
          <div className='flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100'>
            {hasError && (
              <ImageOff
                className='size-12 text-foreground-muted'
                strokeWidth={1.5}
              />
            )}
          </div>
        ) : useNextImage ? (
          <Image
            src={imgUrl}
            alt={alt}
            fill
            priority
            // priority alone no longer implies fetchpriority=high in Next 15 —
            // without it the LCP hero loads at default priority.
            fetchPriority='high'
            sizes='(max-width: 768px) 100vw, 1200px'
            className='object-cover'
            onError={() => setHasError(true)}
          />
        ) : (
          <img
            src={imgUrl}
            alt={alt}
            className='h-full w-full object-cover'
            onError={() => setHasError(true)}
          />
        )}
        {isGenerating && (
          <div className='absolute inset-0 flex items-end bg-gradient-to-t from-black/45 via-black/10 to-transparent p-5'>
            <div className='rounded-full bg-white/90 px-3 py-1 text-sm font-medium text-gray-900 shadow-sm backdrop-blur'>
              Generating hero image
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
